import {
  clearEmrSessionRecord,
  readEmrSessionRecord,
  writeEmrSessionRecord,
} from '~/shell/storage/emrSession'

/**
 * Treated as expired this long before the token's own expiry, so a request that is being prepared as
 * the token lapses is not sent with a bearer the backend will reject.
 */
const EXPIRY_MARGIN_MILLISECONDS = 30 * 1000

const MILLISECONDS_PER_SECOND = 1000

/**
 * The session an EMR launch established, and the suite's one signal for **EMR-embedded mode**.
 *
 * Standalone sessions come from Auth0. An EMR-embedded session is established by the SMART launch instead
 * (Atlas ADR-0002, Atlas ADR-0027). Consumers outside `auth/` do not read this module: `useRegardAuth` is the
 * seam onto whichever session exists, and it asks this one first.
 *
 * Both halves of the launch are recorded, because the exchange that produces the token is itself
 * unauthenticated: `beginLaunch` marks the browser EMR-embedded before the handoff code is redeemed,
 * so the request that redeems it is not sent with a bearer and a dead Auth0 session cannot hijack the
 * launch with a login redirect.
 */
export const emrSession = {
  /**
   * Records that an EMR launch is under way, before any token exists.
   *
   * Replaces any previous launch's record, so a relaunch cannot be served the token of the launch
   * before it.
   */
  beginLaunch: (): void => {
    writeEmrSessionRecord({})
  },

  /**
   * Stores the token a completed launch was issued, expiring it after the lifetime the backend
   * reported for it.
   */
  establish: (accessToken: string, expiresInSeconds: number): void => {
    writeEmrSessionRecord({
      accessToken,
      expiresAt: Date.now() + expiresInSeconds * MILLISECONDS_PER_SECOND,
    })
  },

  /**
   * The EMR-embedded session's access token, or null when there is none to use.
   *
   * Null covers three cases the caller treats alike: no launch, a launch still redeeming its code,
   * and a session whose token has expired. An expired token is not renewable — only the EHR can start
   * a new launch — so the caller's answer is always to send the user back to the EHR rather than to
   * retry.
   */
  getAccessToken: (): string | null => {
    const record = readEmrSessionRecord()
    if (!record?.accessToken || record.expiresAt === undefined) {
      return null
    }

    const isExpired =
      Date.now() >= record.expiresAt - EXPIRY_MARGIN_MILLISECONDS
    return isExpired ? null : record.accessToken
  },

  /**
   * Whether this browser is in **EMR-embedded mode**: an EMR launch has happened here and has not been
   * cleared. Named for where the session came from, not for how the page is framed — an EHR may open
   * the suite in a new tab rather than an `<iframe>`, and a standalone page framed for some unrelated
   * reason is not this.
   *
   * Stays true once the token expires, which is what stops an EMR-embedded user being sent to the Auth0
   * login they have no session for. Cleared only by the launch notice pages, whose copy is what tells
   * the user to relaunch from the EHR.
   */
  isEmrEmbedded: (): boolean => readEmrSessionRecord() !== null,

  /** Ends the EMR-embedded session, taking the browser back to standalone mode. */
  clear: (): void => {
    clearEmrSessionRecord()
  },
}
